import { useEffect, useState } from 'react';
import { Hint, Panel } from './ui/Controls';
import { store, useStore } from '@/state/store';
import { refreshLayout } from '@/engine/pipeline';

/**
 * Copy inputs. Edits are debounced into a layout refresh so typing stays
 * responsive but the canvas keeps up — the engine re-breaks and re-fits lines
 * on every settled keystroke.
 */
export function TextEditor() {
  const title = useStore((s) => s.title);
  const subtitle = useStore((s) => s.subtitle);
  const kicker = useStore((s) => s.kicker);
  const hasVariants = useStore((s) => s.variants.length > 0);

  const [localTitle, setLocalTitle] = useState(title);
  const [localSubtitle, setLocalSubtitle] = useState(subtitle);
  const [localKicker, setLocalKicker] = useState(kicker);

  // Keep local inputs in step when the store changes underneath (undo/redo).
  useEffect(() => setLocalTitle(title), [title]);
  useEffect(() => setLocalSubtitle(subtitle), [subtitle]);
  useEffect(() => setLocalKicker(kicker), [kicker]);

  useEffect(() => {
    if (localTitle === title && localSubtitle === subtitle && localKicker === kicker) {
      return;
    }
    const timer = window.setTimeout(() => {
      store.commit({
        title: localTitle,
        subtitle: localSubtitle,
        kicker: localKicker,
      });
      if (hasVariants) void refreshLayout();
    }, 320);
    return () => window.clearTimeout(timer);
  }, [localTitle, localSubtitle, localKicker, title, subtitle, kicker, hasVariants]);

  return (
    <Panel title="Text">
      <label className="block">
        <span className="field-label">Headline</span>
        <textarea
          value={localTitle}
          onChange={(e) => setLocalTitle(e.target.value)}
          rows={2}
          placeholder="Natural Results Through Modern Rhinoplasty"
          className="text-input resize-none leading-snug"
        />
      </label>

      <label className="mt-3 block">
        <span className="field-label">Subtitle (optional)</span>
        <textarea
          value={localSubtitle}
          onChange={(e) => setLocalSubtitle(e.target.value)}
          rows={2}
          placeholder="A supporting line of detail."
          className="text-input resize-none leading-snug"
        />
      </label>

      <label className="mt-3 block">
        <span className="field-label">Kicker (optional)</span>
        <input
          value={localKicker}
          onChange={(e) => setLocalKicker(e.target.value)}
          placeholder="Aesthetic Surgery"
          className="text-input"
        />
      </label>

      <Hint>
        Your wording is never rewritten. The engine only chooses where the lines
        break, how they are weighted, and which existing words get emphasis.
      </Hint>
    </Panel>
  );
}
